import { isResourcePriceByTime } from "@@/shared/acquire/resource-price/resource-price-by-time";
import type { BookableOptionValue } from "@@/shared/acquire/resource-reference-options/bookable-option-value";
import { convertTimeByGranularity } from "@@/shared/date-time/time-granularity";
import { inMinutes } from "@@/shared/date-time/time-spans";
import { conditionFilter } from "@@/shared/discounts/conditions/discount-condition-filter";
import type { OrderItem_V1 } from "@@/shared/orders/order-items/order-item-v1";
import type { OrderItem_V2 } from "@@/shared/orders/order-items/order-item-v2";
import type { Price} from "@@/shared/prices/price";
import { priceFactory } from "@@/shared/prices/price";
import { subtractFromPrice } from "@@/shared/prices/subtract-from-price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { repeat } from "@@/utilities/repeat";
import { sortBy } from "@@/utilities/sort";
import type { TimeStepDiscount } from "./time-step-discount";

type DiscountPrice = {
    pricePer: Price;
    totalPrice: Price;
};

const timeStepDiscountCalculator =
    (discount: TimeStepDiscount) =>
    <TOrderItem extends OrderItem_V1 | OrderItem_V2 = OrderItem_V2>(
        item: TOrderItem,
        values: BookableOptionValue,
    ) => {
        const entitledItems = conditionFilter(discount, [item], {
            resource: values.resourceId,
        });

        if (
            entitledItems.length !== 1 ||
            item.acquireType !== "BOOKABLE" ||
            values._type !== "QUANTITY_PICKER_BOOKABLE_OPTION_VALUE"
        )
            return undefined;
        //Doing it two times to solve problem with conditionFilter not returning which resource it
        const option = values;

        if (option?._type === "QUANTITY_PICKER_BOOKABLE_OPTION_VALUE") {
            const numberOfBookedTime = inMinutes({
                seconds: item.time.duration,
            });
            if (isResourcePriceByTime(option.resourcePrice)) {
                const stepInMinutes = convertTimeByGranularity(
                    option.resourcePrice.step,
                    option.resourcePrice.granularity,
                    "MINUTE",
                );
                const timeSteps = Math.ceil(numberOfBookedTime / stepInMinutes);
                const totalPrice = sumPrices(
                    repeat(option.resourcePrice.price, timeSteps),
                );
                const totalDiscount = discount.steps
                    .sort(sortBy(s => s.step))
                    .reduce((prev, current) => {
                        if (current.step < numberOfBookedTime)
                            return prev + current.discount;
                        return prev;
                    }, 0);

                const discountPrice = subtractFromPrice(totalPrice, [
                    priceFactory({
                        amountIncludingVat: totalDiscount * 100,
                        vat: totalPrice.vat,
                    }),
                ]);
                return {
                    pricePer: discountPrice,
                    totalPrice: sumPrices(
                        repeat(discountPrice, option.quantity.value),
                    ),
                } as DiscountPrice;
            }
        }

        //console.log("discountPriceResource", discountPrice);
        return undefined;
    };

export { timeStepDiscountCalculator };
export type { DiscountPrice };
